import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Facebook } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiService } from '@/services/api';
import { ContentNodeResponse, ContentNodeTranslation } from '@/types';
import { useAuth } from '@/contexts/AuthContext';

function pickTitle(node: ContentNodeResponse, locale: string): string {
  const tr = node.translations.find((t: ContentNodeTranslation) => t.locale === locale && t.published)
    ?? node.translations.find((t: ContentNodeTranslation) => t.published);
  return tr?.title ?? node.slug;
}

interface SocialLinks {
  facebookUrl?: string;
  instagramUrl?: string;
  threadsUrl?: string;
  logoUrl?: string;
  logoVerticalUrl?: string;
}

const Footer: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [guideNodes, setGuideNodes] = useState<ContentNodeResponse[]>([]);
  const [social, setSocial] = useState<SocialLinks>({});

  useEffect(() => {
    void apiService.contentNodes.getRoots()
      .then(res => setGuideNodes(res.data ?? []))
      .catch(() => {});
    void apiService.seoSettings.get()
      .then(res => { if (res.data) setSocial(res.data); })
      .catch(() => {});
  }, []);

  return (
    <footer className="border-t border-border bg-background">
      <div className="container mx-auto px-4 py-12 md:px-6">
        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-5">
          <div className="lg:col-span-1">
            <Link to="/" className="flex flex-col items-start gap-1">
              <img
                src={social.logoVerticalUrl || social.logoUrl || '/logo-vertical.png'}
                alt="MaidLinks"
                className="h-20 object-contain"
              />
            </Link>
            <p className="mt-4 text-sm text-muted-foreground">
              {t('footer.tagline')}
            </p>
            <div className="mt-4 flex gap-4">
              {social.facebookUrl && (
                <a href={social.facebookUrl} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary">
                  <Facebook className="h-5 w-5" />
                  <span className="sr-only">Facebook</span>
                </a>
              )}
              {social.instagramUrl && (
                <a href={social.instagramUrl} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <span className="sr-only">Instagram</span>
                </a>
              )}
              {social.threadsUrl && (
                <a href={social.threadsUrl} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary">
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M18.263 11.097c-.03-3.486-1.92-5.586-5.111-5.586-2.13 0-3.922.963-4.863 2.499l2.062 1.438c.535-.843 1.272-1.543 2.628-1.543 1.528 0 2.318.85 2.544 2.431a15 15 0 0 0-2.236-.173c-4.125 0-6.068 1.867-6.068 4.336s1.943 3.99 4.804 3.99c3.139 0 5.013-2.115 5.781-4.735.798.361 1.348 1.204 1.348 2.47 0 3.387-3.907 5.232-7.22 5.232-4.885 0-8.077-3.207-8.077-8.424 0-6.392 4.223-10.487 9.9-10.487 3.808 0 5.69 1.671 6.97 3.914l2.108-1.475C21.44 2.078 18.331 0 13.663 0 6.227 0 1.168 5.277 1.168 12.934c0 7 4.953 11.066 10.856 11.066 4.878 0 9.809-2.846 9.809-7.716 0-2.545-1.46-4.231-3.569-5.187m-6.33 4.855c-1.077 0-2.026-.512-2.026-1.453 0-1.483 1.822-1.934 3.606-1.934.678 0 1.34.045 1.927.173-.422 1.927-1.671 3.215-3.508 3.214Z"/>
                  </svg>
                  <span className="sr-only">Threads</span>
                </a>
              )}
            </div>
          </div>

          {user?.role !== 'HELPER' && (
            <div>
              <h3 className="font-semibold text-foreground">{t('footer.lookingFor')}</h3>
              <ul className="mt-4 space-y-2 text-sm">
                <li><Link to="/candidates" className="text-muted-foreground hover:text-primary">{t('footer.domesticHelpers')}</Link></li>
                <li><Link to="/job-postings" className="text-muted-foreground hover:text-primary">{t('footer.employer')}</Link></li>
              </ul>
            </div>
          )}

          <div>
            <h3 className="font-semibold text-foreground">{t('footer.company')}</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/about" className="text-muted-foreground hover:text-primary">{t('footer.about')}</Link></li>
              <li><Link to="/contact" className="text-muted-foreground hover:text-primary">{t('footer.contactUs')}</Link></li>
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-foreground">{t('nav.guides', '指南')}</h3>
            <ul className="mt-4 space-y-2 text-sm">
              {guideNodes.map(node => (
                <li key={node.slug}>
                  <Link to={`/resources/${node.slug}`} className="text-muted-foreground hover:text-primary">
                    {pickTitle(node, i18n.language)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold text-foreground">{t('footer.resources')}</h3>
            <ul className="mt-4 space-y-2 text-sm">
              <li>
                <a href="https://www.immd.gov.hk/hkt/services/visas/foreign_domestic_helpers.html" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary">
                  {t('footer.immigrationDepartment', '入境處')}
                </a>
              </li>
              <li>
                <a href="https://hongkongpcg.dfa.gov.ph/" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary">
                  {t('footer.philippineConsulate', '菲律賓駐香港總領事館')}
                </a>
              </li>
              <li>
                <a href="https://www.kemlu.go.id/hongkong/zh" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary">
                  {t('footer.indonesianConsulate', '印尼領事館')}
                </a>
              </li>
              <li>
                <a href="https://mwohk-ph.com/" target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary">
                  {t('footer.owwa', 'Overseas Workers Welfare Administration (OWWA)')}
                </a>
              </li>
            </ul>
          </div>

        </div>

        <div className="mt-8 border-t border-border pt-8">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm text-muted-foreground text-center">
            <span>© MaidLinks Ethical Services Limited - Licensed Employment Agency in Hong Kong - Licence No. 81653</span>
            <span>·</span>
            <a href="/privacy.pdf" target="_blank" rel="noreferrer" className="hover:text-primary">{t('footer.privacy', '私隱政策')}</a>
            <span>·</span>
            <a href="/terms.pdf" target="_blank" rel="noreferrer" className="hover:text-primary">{t('footer.terms', '條款及細則')}</a>
          </div>
        </div>

      </div>
    </footer>
  );
};

export default Footer;
